import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";

const newKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `k${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

/**
 * ModifierGroupsEditor — self-contained editor for a menu item's modifier groups.
 * Extracted from MenuAdmin.jsx to keep that file focused on catalog CRUD.
 *
 * Props:
 *   value  — Array<{ _key?, name, type, required, multi, options: Array<{_key?, name}> }>
 *   onChange(next) — called with the new list on every change
 */
export default function ModifierGroupsEditor({ value = [], onChange }) {
  const groups = value;

  const addGroup = () =>
    onChange([...(groups || []),
      { _key: newKey(), name: "", type: "add", required: false, multi: true, options: [{ _key: newKey(), name: "" }] }]);

  const updateGroup = (gi, patch) =>
    onChange(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));

  const removeGroup = (gi) => onChange(groups.filter((_, i) => i !== gi));

  const addOption = (gi) =>
    updateGroup(gi, { options: [...groups[gi].options, { _key: newKey(), name: "" }] });

  const updateOption = (gi, oi, name) =>
    updateGroup(gi, {
      options: groups[gi].options.map((o, i) => (i === oi ? { ...o, name } : o)),
    });

  const removeOption = (gi, oi) =>
    updateGroup(gi, { options: groups[gi].options.filter((_, i) => i !== oi) });

  return (
    <div className="border-t border-neutral-200 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="text-sm font-semibold">Ingredient options</Label>
          <p className="text-[11px] text-neutral-500">
            Let customers add or remove ingredients. Surcharges are ignored while prices are hidden.
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={addGroup} data-testid="add-mod-group">
          <Plus className="w-3 h-3 mr-1"/>Add group
        </Button>
      </div>

      <div className="space-y-3">
        {(groups || []).map((g, gi) => (
          <div key={g._key || gi} className="rounded-lg border border-neutral-200 p-3 bg-neutral-50" data-testid={`mod-group-${gi}`}>
            <div className="flex items-center gap-2">
              <Input
                value={g.name}
                onChange={(e) => updateGroup(gi, { name: e.target.value })}
                placeholder="Group name (e.g. Add extras, Remove ingredients)"
                className="flex-1 h-9"
                data-testid={`mod-group-name-${gi}`}
              />
              <select
                value={g.type}
                onChange={(e) => updateGroup(gi, { type: e.target.value })}
                className="h-9 rounded-md border border-neutral-200 bg-white text-xs px-2"
                data-testid={`mod-group-type-${gi}`}
              >
                <option value="add">Add</option>
                <option value="remove">Remove</option>
              </select>
              <button
                type="button"
                onClick={() => removeGroup(gi)}
                className="text-neutral-400 hover:text-red-600 p-1"
                data-testid={`mod-group-remove-${gi}`}
              ><X className="w-4 h-4"/></button>
            </div>

            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={!!g.required}
                  onChange={(e) => updateGroup(gi, { required: e.target.checked })}/>
                Required
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={g.multi !== false}
                  onChange={(e) => updateGroup(gi, { multi: e.target.checked })}/>
                Multi-select
              </label>
            </div>

            <div className="mt-2 space-y-1.5">
              {(g.options || []).map((o, oi) => (
                <div key={o._key || `${gi}-${oi}`} className="flex items-center gap-2">
                  <Input
                    value={o.name}
                    onChange={(e) => updateOption(gi, oi, e.target.value)}
                    placeholder="Option name (e.g. Avocado, Cucumber)"
                    className="h-8"
                    data-testid={`mod-opt-${gi}-${oi}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(gi, oi)}
                    className="text-neutral-400 hover:text-red-600 p-1"
                  ><X className="w-3 h-3"/></button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => addOption(gi)}
                className="text-[hsl(var(--primary))] h-7 px-2"
                data-testid={`add-mod-opt-${gi}`}
              ><Plus className="w-3 h-3 mr-1"/>Option</Button>
            </div>
          </div>
        ))}

        {(!groups || groups.length === 0) && (
          <p className="text-xs text-neutral-400 italic">No ingredient groups yet.</p>
        )}
      </div>
    </div>
  );
}
